import { useState } from 'react';
import { Download, X, Share } from 'lucide-react';
import { usePwaInstall } from '../hooks/usePwaInstall';

function detectBrowser(): 'chrome' | 'edge' | 'safari' | 'firefox' | 'other' {
  const ua = navigator.userAgent;
  if (/CriOS/.test(ua)) return 'chrome';
  if (/EdgA|Edg\//.test(ua)) return 'edge';
  if (/Chrome/.test(ua)) return 'chrome';
  if (/Safari/.test(ua)) return 'safari';
  if (/Firefox/.test(ua)) return 'firefox';
  return 'other';
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

const INSTRUCTIONS: Record<string, { title: string; steps: string[] }> = {
  safari: {
    title: 'Installer sur iPhone / Safari',
    steps: [
      "Appuyer sur l'icône Partager ↑ en bas de l'écran",
      'Défiler et appuyer sur « Sur l’écran d’accueil »',
      'Appuyer sur « Ajouter » en haut à droite',
    ],
  },
  firefox: {
    title: 'Installer sur Firefox',
    steps: [
      'Appuyer sur le menu ⋮ (3 points) en haut à droite',
      'Sélectionner « Installer »',
    ],
  },
  other: {
    title: "Installer l'application",
    steps: [
      'Ouvrir dans Chrome ou Edge pour une installation facile',
      'Ou utiliser le menu du navigateur → « Ajouter à l’écran d’accueil »',
    ],
  },
};

export function PwaInstallButton() {
  const { canInstall, isInstalled, install } = usePwaInstall();
  const [showModal, setShowModal] = useState(false);

  if (isInstalled) return null;

  const browser = detectBrowser();
  const ios = isIOS();

  if (canInstall) {
    return (
      <button
        type="button"
        onClick={() => void install()}
        className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-slate-700 dark:bg-slate-600 dark:hover:bg-slate-500"
      >
        <Download size={13} strokeWidth={2.5} />
        Installer
      </button>
    );
  }

  const instructions = INSTRUCTIONS[ios ? 'safari' : browser] ?? INSTRUCTIONS.other;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/50"
      >
        <Download size={13} strokeWidth={2.5} />
        Installer
      </button>

      {showModal ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 pb-4 sm:items-center sm:pb-0"
          onClick={() => setShowModal(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-md dark:bg-slate-800 dark:shadow-none dark:ring-1 dark:ring-slate-600"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pwa-install-title"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3
                id="pwa-install-title"
                className="text-[15px] font-semibold text-slate-800 dark:text-slate-100"
              >
                {instructions.title}
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label="Fermer"
              >
                <X size={16} />
              </button>
            </div>
            <ol className="space-y-3">
              {instructions.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-800 text-[11px] font-bold text-white dark:bg-slate-600">
                    {i + 1}
                  </span>
                  <span className="text-[13px] text-slate-700 dark:text-slate-300">{step}</span>
                </li>
              ))}
            </ol>
            {ios ? (
              <div className="mt-4 flex items-center justify-center gap-2 text-[12px] text-slate-400">
                <Share size={14} aria-hidden />
                <span>Chercher l&apos;icône en bas de Safari</span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
